import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarCheck, Plus, CheckCircle2, XCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { formatDate, formatDateTime } from "@/lib/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export function CheckInsSection({ c, me }) {
  const qc = useQueryClient();
  const plan = c.plan;
  const [open, setOpen] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [notes, setNotes] = useState("");
  const [initials, setInitials] = useState("");
  const [progress, setProgress] = useState(() => (plan?.goals || []).map((_, i) => ({ goal_index: i, progress: "", on_track: true })));

  const submit = useMutation({
    mutationFn: async () => api.post(`/cases/${c.id}/checkins`, {
      scheduled_date: scheduledDate || null, goal_progress: progress, notes, initials,
    }),
    onSuccess: () => {
      toast.success("Check-in logged");
      setOpen(false); setNotes(""); setInitials(""); setScheduledDate("");
      setProgress((plan?.goals || []).map((_, i) => ({ goal_index: i, progress: "", on_track: true })));
      qc.invalidateQueries({ queryKey: ["case", c.id] }); qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  if (!plan || plan.status !== "active") return null;
  const canLog = me.role === "manager" || me.role === "leadership";
  const checkins = plan.checkins || [];
  const pending = (plan.scheduled_checkins || []).filter((s) => !s.completed);

  const valid = notes.trim().length >= 3 && initials.trim() && progress.every((p) => p.progress.trim().length >= 5);

  return (
    <Card className="bg-patria-surface border-patria-muted/15 p-6" data-testid="checkins-section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-patria-green/10 flex items-center justify-center"><CalendarCheck className="h-4 w-4 text-patria-green" /></div>
          <div>
            <h3 className="font-heading font-semibold text-patria-ink">Stage 3 · Check-ins</h3>
            <p className="text-xs text-patria-muted">Log progress against each goal on scheduled dates.</p>
          </div>
        </div>
        {canLog && !open && (
          <Button size="sm" onClick={() => setOpen(true)} data-testid="checkin-open" className="rounded-full bg-patria-terracotta hover:bg-patria-terracottaDeep text-patria-surface"><Plus className="h-4 w-4 mr-1" /> Log check-in</Button>
        )}
      </div>

      {open && (
        <div className="rounded-xl border border-patria-muted/20 bg-patria-sage/30 p-4 space-y-4 mb-5" data-testid="checkin-form">
          {pending.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-patria-ink">Which scheduled check-in?</Label>
              <Select value={scheduledDate} onValueChange={setScheduledDate}>
                <SelectTrigger className="bg-patria-surface border-patria-muted/30" data-testid="checkin-scheduled-date"><SelectValue placeholder="Select scheduled date (or leave blank for today)" /></SelectTrigger>
                <SelectContent className="bg-patria-surface">{pending.map((s) => <SelectItem key={s.date} value={s.date}>{formatDate(s.date)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-3">
            {plan.goals.map((g, i) => (
              <div key={i} className="rounded-lg bg-patria-surface p-3 space-y-2" data-testid={`checkin-goal-${i}`}>
                <p className="text-sm font-medium text-patria-ink">Goal {i + 1}: {g.expectation}</p>
                <p className="text-xs text-patria-muted">Measure: {g.measure}</p>
                <Textarea value={progress[i].progress} onChange={(e) => setProgress((arr) => arr.map((x, j) => (j === i ? { ...x, progress: e.target.value } : x)))} rows={2} placeholder="Specific progress against this measure…" data-testid={`checkin-progress-${i}`} className="bg-patria-surface border-patria-muted/30" />
                <div className="flex items-center gap-2">
                  <Switch checked={progress[i].on_track} onCheckedChange={(v) => setProgress((arr) => arr.map((x, j) => (j === i ? { ...x, on_track: v } : x)))} data-testid={`checkin-ontrack-${i}`} className="data-[state=checked]:bg-patria-green" />
                  <span className={`text-sm ${progress[i].on_track ? "text-patria-green" : "text-patria-terracottaDeep"}`}>{progress[i].on_track ? "On track" : "Off track"}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
            <div className="space-y-1.5"><Label className="text-patria-ink">Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Overall notes for this check-in…" data-testid="checkin-notes" className="bg-patria-surface border-patria-muted/30" /></div>
            <div className="space-y-1.5"><Label className="text-patria-ink">Initials</Label><Input value={initials} onChange={(e) => setInitials(e.target.value)} placeholder="e.g., MB" data-testid="checkin-initials" className="bg-patria-surface border-patria-muted/30" /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} className="rounded-full border-patria-muted/30" data-testid="checkin-cancel">Cancel</Button>
            <Button size="sm" onClick={() => submit.mutate()} disabled={!valid || submit.isPending} data-testid="checkin-submit" className="rounded-full bg-patria-green hover:bg-patria-greenDeep text-patria-surface">Save check-in</Button>
          </div>
        </div>
      )}

      {checkins.length === 0 ? (
        <p className="text-sm text-patria-muted">No check-ins logged yet.</p>
      ) : (
        <div className="space-y-3" data-testid="checkins-log">
          {checkins.slice().reverse().map((ci, idx) => (
            <div key={ci.logged_at || idx} className="rounded-xl border border-patria-muted/15 p-4" data-testid={`checkin-entry-${idx}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-patria-ink">Check-in · {formatDate(ci.date)}</p>
                <span className="text-xs text-patria-muted">{ci.author?.name} ({ci.initials}) · {formatDateTime(ci.logged_at)}</span>
              </div>
              <div className="mt-2 space-y-1.5">
                {ci.goal_progress.map((gp, gi) => (
                  <div key={gi} className="flex items-start gap-2 text-sm">
                    {gp.on_track ? <CheckCircle2 className="h-4 w-4 text-patria-green mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-patria-terracottaDeep mt-0.5 shrink-0" />}
                    <span className="text-patria-ink"><span className="text-patria-muted">Goal {gp.goal_index + 1}:</span> {gp.progress}</span>
                  </div>
                ))}
              </div>
              {ci.notes && <p className="text-sm text-patria-muted mt-2 italic">{ci.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
